import { useQuery } from '@tanstack/react-query'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

import { fetchBridgeChains } from '@/api/modules'
import GradientWrapper from '@/common/GradientWrapper'
import { Chain } from '@/generated/proto/bridge/models/chain'
import { queryKeys } from '@/queries/keys'
import {
  ControlledUiSelect,
  UiSelectContent,
  UiSelectItem,
  UiSelectTrigger,
  UiSelectValue,
} from '@/ui/UiSelect'

interface ChainSelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  onValueChange?: (chainId: string) => void
  onChainSelect?: (chain: Chain) => void
  disabled?: boolean
  placeholder?: string
}

export default function ChainSelect<TFieldValues extends FieldValues>({
  control,
  name,
  onValueChange,
  onChainSelect,
  disabled = false,
  placeholder = 'Select a chain',
}: ChainSelectProps<TFieldValues>) {
  const { data: chains = [], isLoading: loadingChains } = useQuery({
    queryKey: queryKeys.chains.list(),
    queryFn: fetchBridgeChains,
  })

  return (
    <ControlledUiSelect
      name={name}
      control={control}
      onValueChange={chainId => {
        onValueChange?.(chainId)
        const selectedChain = chains.find(chain => chain.id === chainId)
        if (selectedChain) {
          onChainSelect?.(selectedChain)
        }
      }}
      disabled={disabled || loadingChains}
    >
      <GradientWrapper>
        <UiSelectTrigger
          size='xl'
          className='bg-input border-border w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
        >
          <UiSelectValue
            placeholder={loadingChains ? 'Loading...' : placeholder}
          />
        </UiSelectTrigger>
      </GradientWrapper>

      <UiSelectContent>
        {chains.map(chain => (
          <UiSelectItem key={chain.id} value={chain.id}>
            <div className='flex items-center gap-2'>
              <span className='font-medium'>{chain.name}</span>
              <span className='text-muted-foreground text-xs'>
                ({chain.id})
              </span>
            </div>
          </UiSelectItem>
        ))}
      </UiSelectContent>
    </ControlledUiSelect>
  )
}
